import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Play, Filter, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useVideos } from '../hooks/useContent';

const categories = [
  { id: 'all', label: 'All Videos' },
  { id: 'podcast', label: 'Podcasts' },
  { id: 'interview', label: 'Interviews' },
  { id: 'clip', label: 'Clips' },
];

export default function Videos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || 'all';
  const [activeCategory, setActiveCategory] = useState(initialCategory);

  // Fetch videos from Contentful (with static fallback)
  const { videos: videosData, isLoading, error } = useVideos();

  const filteredVideos = useMemo(() => {
    if (activeCategory === 'all') return videosData;
    return videosData.filter((video) => video.category === activeCategory);
  }, [activeCategory, videosData]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  return (
    <div data-testid="videos-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
              Watch & Listen
            </p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-brand-primary mb-6">
              Videos & Podcasts
            </h1>
            <p className="text-slate-600 text-lg">
              Explore my collection of podcasts, interviews, and short clips covering leadership,
              personal growth, and organizational transformation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-slate-100 sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <Filter className="w-5 h-5 text-slate-400 flex-shrink-0" />
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={activeCategory === category.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => handleCategoryChange(category.id)}
                className={`rounded-full flex-shrink-0 ${
                  activeCategory === category.id
                    ? 'bg-brand-primary text-white'
                    : 'text-slate-600 hover:text-brand-primary'
                }`}
                data-testid={`filter-${category.id}`}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
              <span className="ml-2 text-slate-600">Loading videos...</span>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="text-center py-16">
              <p className="text-red-500 mb-4">Failed to load videos: {error}</p>
              <Button variant="outline" onClick={() => window.location.reload()} className="rounded-full">
                Try Again
              </Button>
            </div>
          )}

          {/* Videos Grid */}
          {!isLoading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group"
                data-testid={`video-card-${video.id}`}
              >
                <Link to={`/videos/${video.slug}`} className="block">
                  <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-lg">
                          <Play className="w-6 h-6 text-brand-primary ml-1" />
                        </div>
                      </div>
                      <span className="absolute top-4 left-4 bg-brand-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                        {video.category}
                      </span>
                      <span className="absolute bottom-4 right-4 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </span>
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-xl font-bold text-brand-primary mb-2 group-hover:text-brand-secondary transition-colors">
                        {video.title}
                      </h3>
                      <p className="text-slate-600 text-sm line-clamp-2">{video.description}</p>
                      <div className="mt-4 text-xs text-slate-500">{video.date}</div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          )}

          {!isLoading && !error && filteredVideos.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No videos found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
